import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box} from 'views/components';
import {TimezoneAutocomplete} from 'views/module';

import CalendarDisplay from './calendar/CalendarDisplay';
import ViewEvent from './event_manager/view/ViewEvent';

import momentTZ from 'moment-timezone';

const Schedule = (props) => {
  const [model, setModel] = React.useState({});
  const [loadedMonths, setLoadedMonths] = React.useState(new Set());
  const [selectedTimezone, setSelectedTimezone] = React.useState(momentTZ.tz.guess());
  const [reload, setReload] = React.useState(0);

  const scrollContainerRef = React.useRef(null);

  const parentReload = props.refresh;
  const caregiverScheduleEvents = props.caregiverScheduleEvents;
  const caregiverBackend = props.caregiverBackend;

  // Reload calendar when refresh button is triggered
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Skip the first run
      return;
    }
    // Reload calendar when refresh button is triggered
    reloadCalendar();
  }, [parentReload]);

  // Scroll to the bottom
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollIntoView({alignToTop: true, behavior: 'smooth', block: 'start'});
    }
  }, [scrollContainerRef]);

  const reloadCalendar = () => {
    setLoadedMonths(new Set());
    setReload((e) => e + 1);
  };

  const loadMonthlyEvents = (monthsToLoad) => {
    // Filter months that haven't been loaded yet
    const unloadedMonths = monthsToLoad.filter(
        ({year, month}) => !loadedMonths.has(`${year}-${month}`),
    );
    // Mark these months as loaded
    setLoadedMonths((prevLoadedMonths) => {
      const updatedLoadedMonths = new Set(prevLoadedMonths);
      unloadedMonths.forEach(({year, month}) => updatedLoadedMonths.add(`${year}-${month}`));
      return updatedLoadedMonths;
    });

    if (unloadedMonths.length) {
      const months = unloadedMonths.map((e) => ({year: `${e.year}`, month: `${e.month + 1}`}));
      caregiverBackend.schedule.loadMonthlyEvents(months, true);
    }
  };

  const openDialogs = {
    close: () => {
      setModel({});
    },
    openEvent: (eventRn, event) => {
      setModel({action: 'OPEN_EVENT', eventRn, event});
    },
  };

  const popModelGenerator = React.useCallback(() => {
    let popModel = null;
    if (model.action === 'OPEN_EVENT') {
      popModel = <ViewEvent
        onClose={openDialogs.close}
        eventRn={model.eventRn}
        event={model.event}
      />;
    }
    return popModel;
  }, [model]);


  return (
    <Stack
      sx={{
        p: 2,
      }}
      spacing={1}
      ref={scrollContainerRef}
    >
      {popModelGenerator()}
      <Stack direction='row' spacing={{md: 2, xs: 1}} justifyContent='space-between' alignItems='center'>
        <TimezoneAutocomplete
          selectedTimezone={selectedTimezone}
          onTimezoneChange={setSelectedTimezone}
        />
      </Stack>
      <Box
        sx={{
          height: '1100px',
          width: '100%',
        }}
      >
        <CalendarDisplay
          loadMonthlyEvents={loadMonthlyEvents}
          events={Object.values(caregiverScheduleEvents)}
          openEvent={openDialogs.openEvent}
          reload={reload}
          viewingTimezone={selectedTimezone}
        />
      </Box>
    </Stack>
  );
};

Schedule.propTypes = {
  refresh: PropTypes.number.isRequired,
  caregiverScheduleEvents: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
};
export default React.memo(Schedule);
